import { useCallback, useMemo, useState } from 'react';
import { Snackbar, Alert, AlertProps } from '@mui/material';

interface UseSnackbarReturnType {
  showSnackbar: (message: string, severity?: AlertProps['severity']) => void;
  resetSnackbar: () => void;
  SnackbarComponent: JSX.Element | null;
}

const useSnackbar = (): UseSnackbarReturnType => {
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] =
    useState<AlertProps['severity']>('success');

  const showSnackbar = useCallback(
    (message: string, severity: AlertProps['severity'] = 'success') => {
      setSnackbarMessage(message);
      setSnackbarSeverity(severity);
      setSnackbarOpen(true);
    },
    [],
  );
  const handleSnackbarClose = useCallback(
    (event?: React.SyntheticEvent | Event, reason?: string) => {
      if (reason === 'clickaway') return;

      setSnackbarOpen(false);
    },
    [],
  );

  const resetSnackbar = useCallback(() => {
    setTimeout(() => {
      setSnackbarOpen(false);
      setSnackbarMessage('');
      setSnackbarSeverity('success');
    }, 1000);
  }, []);

  const SnackbarComponent = useMemo(() => {
    if (!snackbarOpen) return null;

    return (
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    );
  }, [snackbarOpen, snackbarMessage, snackbarSeverity, handleSnackbarClose]);

  return { showSnackbar, SnackbarComponent, resetSnackbar };
};

export default useSnackbar;
